// The Dashboard family's wire shapes — one per member, served by BOTH doors:
// `apps/api/src/routes/dashboards.ts` and `packages/mcp/src/army/dashboard.ts`.
//
// All three bodies were written twice. `maskDashboard` stood in both files,
// identical but for two comments; `maskWidget` and `maskDashboardWidget` had
// byte-identical bodies under different names; `maskDetail(d, widgets)` and
// `maskDashboardDetail(detail)` produced the same object from different
// arguments. Nothing mechanical held them equal, so a field added on one door
// was a field missing on the other — the drift `resource.ts` exists to make
// unrepresentable. Read it for the seam and its two constraints (no
// `.transform()`; the wire is snake_case while the entity is camelCase).
//
// The projections take the fields they READ, not the `Dashboard` /
// `DashboardWidget` entities, so both doors satisfy them as they are: `apps/api`
// carries an extra `outcome` on its detail and the MCP port does not. It also
// states the projection's real appetite — nothing here reads `accountId`, so
// nothing here can start to.

import { z } from 'zod';
import { dashboardScope } from '../entities/dashboard';
import {
  type DashboardLayout,
  DashboardLayoutSchema,
  DashboardScopeSchema,
  type WidgetSpec,
  WidgetSpecSchema,
} from '../value-objects/dashboard';
import {
  type DashboardId,
  DashboardIdSchema,
  type DashboardWidgetId,
  DashboardWidgetIdSchema,
} from '../value-objects/ids';
import { defineResource } from './resource';

/** The camelCase shape the board projections read. A `dashboards` row satisfies it. */
export type DashboardEntity = {
  id: DashboardId;
  name: string;
  ownerUserId: string | null;
  builtin: boolean;
  layout: DashboardLayout;
  createdAt: Date;
  updatedAt: Date;
};

/** The camelCase shape the widget projection reads. A `dashboard_widgets` row satisfies it. */
export type DashboardWidgetEntity = {
  id: DashboardWidgetId;
  spec: WidgetSpec;
  createdAt: Date;
  updatedAt: Date;
};

/**
 * A board and its widgets — the ONE argument the detail projection takes.
 *
 * The two doors reached the same body from different argument shapes: REST
 * passed the board and its widgets separately, MCP passed a detail object. A
 * `Resource` serializes one entity, so the pair is the input, because it is the
 * shape both callers already hold — `DashboardWithWidgets` (`apps/api`) and
 * `DashboardDetail` (`@developerz/mcp`) each satisfy it unchanged, and the one
 * REST site holding two loose variables pairs them at the call site. Adapting
 * there keeps the detail body a resource like its two siblings.
 */
export type DashboardDetailEntity = {
  dashboard: DashboardEntity;
  widgets: DashboardWidgetEntity[];
};

/** Summary body — derived scope, ISO timestamps, no layout dump. */
export const DashboardWireSchema = z.object({
  id: DashboardIdSchema,
  name: z.string(),
  /** Derived router scope (org | user | custom) — never a stored column. */
  scope: DashboardScopeSchema,
  owner_user_id: z.uuid().nullable(),
  builtin: z.boolean(),
  /** Placed-widget count (layout refs) — the at-a-glance size; the full layout
   * rides the detail body. */
  widget_count: z.number().int().nonnegative(),
  created_iso: z.iso.datetime(),
  updated_iso: z.iso.datetime(),
});

/** What a client should type against for a dashboard summary body. */
export type DashboardWire = z.infer<typeof DashboardWireSchema>;

/** One widget — its read-only spec + timestamps. */
export const DashboardWidgetWireSchema = z.object({
  id: DashboardWidgetIdSchema,
  spec: WidgetSpecSchema,
  created_iso: z.iso.datetime(),
  updated_iso: z.iso.datetime(),
});

/** What a client should type against for one widget body. */
export type DashboardWidgetWire = z.infer<typeof DashboardWidgetWireSchema>;

/** Full detail — the summary + the concrete layout + every widget spec. Built by
 * EXTENDING the summary, so the two can never disagree about a shared field. */
export const DashboardDetailWireSchema = DashboardWireSchema.extend({
  layout: DashboardLayoutSchema,
  widgets: z.array(DashboardWidgetWireSchema),
});

/** What a client should type against for a dashboard detail body. */
export type DashboardDetailWire = z.infer<typeof DashboardDetailWireSchema>;

export const DashboardResource = defineResource<DashboardEntity, typeof DashboardWireSchema>({
  schema: DashboardWireSchema,
  serialize: (d) => ({
    id: d.id,
    name: d.name,
    scope: dashboardScope(d),
    owner_user_id: d.ownerUserId,
    builtin: d.builtin,
    widget_count: d.layout.rows.length,
    created_iso: d.createdAt.toISOString(),
    updated_iso: d.updatedAt.toISOString(),
  }),
});

export const DashboardWidgetResource = defineResource<
  DashboardWidgetEntity,
  typeof DashboardWidgetWireSchema
>({
  schema: DashboardWidgetWireSchema,
  serialize: (w) => ({
    id: w.id,
    spec: w.spec,
    created_iso: w.createdAt.toISOString(),
    updated_iso: w.updatedAt.toISOString(),
  }),
});

export const DashboardDetailResource = defineResource<
  DashboardDetailEntity,
  typeof DashboardDetailWireSchema
>({
  schema: DashboardDetailWireSchema,
  serialize: (detail) => ({
    ...DashboardResource.serialize(detail.dashboard),
    layout: detail.dashboard.layout,
    widgets: detail.widgets.map((w) => DashboardWidgetResource.serialize(w)),
  }),
});
